/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {dlsSidebarWidth} from '../components/navigation/NavigationStore';
import Switch from '../components/switch/Switch';
import Tooltip from '../components/tooltip/Tooltip';
import Render from '../render/Render';
import {DLSShadowStyle} from '../types/types';
import {logThemeToggleClick} from './DLSThemeLogger';
import {dlsColor, dlsShadow} from './theme';
import {multiThemeEnabled, setCurrentTheme} from './ThemeStore';
import {DLSThemes} from './types';
import {useThemeContext} from './useThemeContext';

export interface NewIonThemeSwitchProps {
  collapsed?: boolean;
}

// the switch's own label wrapper sits flush in its slot ...
const SWITCH_MARGIN = '0px';

// the side nav footer items' (Alerts, Help, Profile) own sizes and states,
// so the switch reads as one of them - see `NavigationList` ...
const useStyles = makeStyles(() => ({
  // spans the nav's full width however its footer lays out its children - a
  // footer that aligns them to the start otherwise shrinks this to the switch
  // and leaves it against the rail's left edge ...
  outer : {
    alignSelf : 'stretch',
    boxSizing : 'border-box',
    width     : '100%'
  },
  // the expanded footer list's own 8px inset, shared with the items above -
  // it sits right after them, so the list's bottom inset is taken back ...
  footerRow : {
    marginTop : -8,
    padding   : '0 8px 8px'
  },
  item : {
    alignItems              : 'center',
    borderRadius            : 8,
    boxSizing               : 'border-box',
    cursor                  : 'pointer',
    display                 : 'flex',
    minHeight               : 44,
    padding                 : '6px 8px',
    transition              : 'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    width                   : 224,
    '@media (hover: hover)' : {
      '&:hover $itemFrame' : {
        backgroundColor : dlsColor('white'),
        border          : `1px solid ${dlsColor('grey5')}`,
        borderRadius    : 8,
        boxShadow       : '0 1px 2px rgba(0, 0, 0, 0.12)'
      },
      '&:hover $text' : {
        color : dlsColor('lightTeal')
      }
    },
    '&:active $itemFrame' : {
      backgroundColor : dlsColor('paleBlue'),
      border          : `1px solid ${dlsColor('grey5')}`,
      boxShadow       : dlsShadow(3)
    },
    '&:active $text' : {
      color : dlsColor('darkTeal')
    },
    // the keyboard focus ring, as the footer items' own ...
    '&:has(input:focus-visible)' : {
      boxShadow : `inset 0 0 0 2px ${dlsColor('aaBlue')}`,
      outline   : 'none'
    },
    // a browser without `:has()` rings the row while its switch has focus at
    // all - a click's included - rather than not at all ...
    '@supports not selector(:has(*))' : {
      '&:focus-within' : {
        boxShadow : `inset 0 0 0 2px ${dlsColor('aaBlue')}`,
        outline   : 'none'
      }
    }
  },
  // the collapsed rail's full width, the switch's own 40px frame centred in
  // it. The nav's own item rules reach this row in the app, so these are
  // held with a doubled class, and the frame centres itself as well ...
  collapsedItem : {
    '&&' : {
      justifyContent : 'center',
      padding        : '6px 0',
      width          : '100%'
    }
  },
  itemFrame : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : '1px solid transparent',
    borderRadius    : 8,
    boxSizing       : 'border-box',
    display         : 'flex',
    gap             : 8,
    height          : 32,
    padding         : '4px 8px',
    width           : 208
  },
  // as wide as the switch and its own 1px hover border, so the switch sits
  // exactly inside it ...
  collapsedItemFrame : {
    '&&' : {
      flex           : 'none',
      justifyContent : 'center',
      margin         : '0 auto',
      padding        : 0,
      width          : 'auto'
    }
  },
  text : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 500,
    lineHeight : '20px',
    whiteSpace : 'nowrap'
  },
  label : {
    flexGrow : 1
  },
  switchSlot : {
    alignItems                    : 'center',
    display                       : 'flex',
    flex                          : 'none',
    height                        : 24,
    justifyContent                : 'center',
    width                         : 44,
    // MUI's own label wrapper keeps its -11px/16px margins over the switch's
    // own, and its empty text label still takes space after the switch -
    // 8px in the app - which widened the wrapper past the 40px switch and
    // left the switch off centre. The margins are cleared and the empty
    // label is not laid out, so the wrapper is exactly the switch ...
    '& .MuiFormControlLabel-root' : {
      margin : 0
    },
    '& .MuiFormControlLabel-label' : {
      display : 'none'
    }
  },
  collapsedSwitchSlot : {
    width : 40
  }
}));

// the tooltip opens above the switch row ...
const TOOLTIP_PLACEMENT = 'top' as const;

// the current theme tooltip's look, held to the expanded side nav's width -
// expanded or collapsed alike. MUI's own tooltip rule (its font and a 300px
// max width) is injected after this sheet - the doubled class outranks it ...
const useTooltipStyles = makeStyles(() => {
  const width = dlsSidebarWidth(false);
  return {
    tooltip : {
      '&&' : {
        alignItems      : 'center',
        backgroundColor : dlsColor('grey1'),
        borderRadius    : 8,
        boxShadow       : dlsShadow(1),
        boxSizing       : 'border-box',
        color           : dlsColor('white'),
        display         : 'flex',
        filter          : dlsShadow(1, DLSShadowStyle.DropShadow),
        fontFamily      : 'Inter',
        // the themes' own MUI tooltip override sets 16px/24px as !important ...
        fontSize        : '12px !important',
        fontWeight      : 500,
        justifyContent  : 'center',
        letterSpacing   : 0,
        lineHeight      : '16px !important',
        maxWidth        : width,
        minWidth        : width,
        padding         : '4px 8px',
        textAlign       : 'center',
        width
      }
    },
    arrow : {
      color : dlsColor('grey1')
    }
  };
});

/**
 * Displays the current theme side nav's "New iOn" switch - on while the
 * current theme is showing; turning it off returns the application to the
 * legacy theme. It takes the side nav footer items' own sizes and states -
 * a click anywhere on it switches, as on any item. The expanded nav shows
 * its label and its "On" state beside it; the collapsed nav shows the switch
 * alone, centred. A tooltip above the row, as
 * wide as the expanded nav, says the legacy theme stays a switch away.
 * Renders only while multiple themes are enabled.
 *
 * ```typescript
 *
 *   import NewIonThemeSwitch from '@ion/dls/src/theme/NewIonThemeSwitch';
 *   ...
 *
 *   <NewIonThemeSwitch collapsed={isCollapsed} />
 *
 * ```
 *
 * @param props The nav's collapsed state.
 * @returns The switch row, or nothing while only one theme is enabled.
 */
export const NewIonThemeSwitch = ({collapsed = false}: NewIonThemeSwitchProps) => {
  const classes        = useStyles();
  const tooltipClasses = useTooltipStyles();
  const label          = translate('common:theme.newIon');
  const {
    theme,
    multiThemeEnabled : isMultiThemeEnabled
  } = useThemeContext();

  const onSwitchTheme = useCallback(function() {
    if (multiThemeEnabled.value) {
      logThemeToggleClick(theme, DLSThemes.legacy);
      setCurrentTheme(DLSThemes.legacy);
    }
  }, [theme]);

  const themeSwitch = (
    <span className={collapsed ? `${classes.switchSlot} ${classes.collapsedSwitchSlot}` : classes.switchSlot}>
      <Switch checked
        isDarkLabel={false}
        marginLeft={SWITCH_MARGIN}
        marginRight={SWITCH_MARGIN}
        inputProps={{'aria-label': label}}
        onChange={onSwitchTheme} />
    </span>
  );

  const item = (
    <div className={collapsed ? `${classes.item} ${classes.collapsedItem}` : classes.item}
      onClick={onSwitchTheme}>
      <div className={collapsed ? `${classes.itemFrame} ${classes.collapsedItemFrame}` : classes.itemFrame}>
        <Render>
          <Render.If condition={collapsed}>
            {themeSwitch}
          </Render.If>
          <Render.Else>
            <span className={`${classes.text} ${classes.label}`}>
              {label}
            </span>
            {themeSwitch}
            <span className={classes.text}>
              {translate('common:switchStatus.on')}
            </span>
          </Render.Else>
        </Render>
      </div>
    </div>
  );

  return isMultiThemeEnabled ? (
    <Tooltip arrow
      externalClasses={tooltipClasses}
      placement={TOOLTIP_PLACEMENT}
      title={translate('common:theme.newIonHint')}>
      <div className={collapsed ? classes.outer : `${classes.outer} ${classes.footerRow}`}>
        {item}
      </div>
    </Tooltip>
  ) : null;
};

export default memo(NewIonThemeSwitch);
